import React, { useState, useEffect, useRef, useCallback } from "react";
import { EditorRoot, EditorContent } from "novel";
import {
  StarterKit,
  TiptapLink,
  TiptapImage,
  TiptapUnderline,
  TaskList,
  TaskItem,
} from "novel";
import { BubbleMenu } from "@tiptap/react";
import Color from "@tiptap/extension-color";
import TextStyle from "@tiptap/extension-text-style";
import Bold from "@tiptap/extension-bold";
import TextAlign from "@tiptap/extension-text-align";
import { UploadImagesPlugin } from "novel";
import { uploadFn, insertImageAsBase64 } from "../files/ImageUpload";
import { errorToast } from "../../util/toastNotifications";
import { ImageIcon, Link as LinkIcon } from "lucide-react";
import useLinkHoverPopup from "../../hooks/useLinkHoverPopup";
import useClickOutside from "../../hooks/useClickOutside";
import { CodeBlockLowlight } from "novel";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faList,
  faListOl,
  faQuoteLeft,
  faAlignCenter,
  faAlignLeft,
  faAlignRight,
} from "@fortawesome/free-solid-svg-icons";
import "../../styles/components/novel-editor.scss";
import clsx from "clsx";

import { createLowlight } from "lowlight";

const cx = clsx;

import javascript from "highlight.js/lib/languages/javascript";
import xml from "highlight.js/lib/languages/xml";
import css from "highlight.js/lib/languages/css";
import python from "highlight.js/lib/languages/python";

const lowlight = createLowlight();

lowlight.register({ javascript, xml, css, python });
lowlight.registerAlias({ javascript: "js", xml: "html" });

const BubbleMenuItem = ({ onClick, children, className = "", editor }) => {
  const buttonRef = useRef(null);

  useEffect(() => {
    const button = buttonRef.current;
    if (button && editor && onClick) {
      const handleMouseDown = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (!editor.isFocused) {
          editor.view.focus();
        }
        onClick(editor);
      };

      button.addEventListener("mousedown", handleMouseDown, true);

      return () => {
        button.removeEventListener("mousedown", handleMouseDown, true);
      };
    }
  }, [editor, onClick]);

  return (
    <button
      ref={buttonRef}
      type="button"
      className={`novel-editor-bubble-item ${className}`}
    >
      {children}
    </button>
  );
};

const tiptapImage = TiptapImage.extend({
  addProseMirrorPlugins() {
    return [
      UploadImagesPlugin({
        uploadFn,
        imageClass: cx("opacity-40 rounded-lg border border-stone-200"),
      }),
    ];
  },
}).configure({
  allowBase64: true,
  HTMLAttributes: {
    class: cx("rounded-lg border border-muted"),
  },
});

const CustomBold = Bold.extend({
  addInputRules() {
    const rules = [];

    rules.push({
      find: /(?:^|\s)(\*\*\*(?!\s+\*\*\*)((?:[^*]+))\*\*\*(?!\s+\*\*\*))$/,
      handler: ({ state, range, match }) => {
        const text = match[2];
        const fullMatch = match[0];
        const asteriskPart = match[1];

        const hasLeadingSpace = fullMatch.length > asteriskPart.length;

        const start = hasLeadingSpace ? range.from + 1 : range.from;
        const end = start + asteriskPart.length;

        const { tr } = state;

        tr.delete(start, end);

        tr.insertText(text, start);

        const boldMark = state.schema.marks.bold.create();
        const italicMark = state.schema.marks.italic.create();

        tr.addMark(start, start + text.length, boldMark);
        tr.addMark(start, start + text.length, italicMark);

        return tr;
      },
    });

    const parentRules = this.parent?.() || [];
    rules.push(...parentRules);

    return rules;
  },
});

const CustomLink = TiptapLink.extend({
  addInputRules() {
    const rules = [];

    rules.push({
      find: /\[([^\]]+)\]\(([^)]+)\)$/,
      handler: ({ state, range, match }) => {
        const linkText = match[1];
        let url = match[2];

        if (!url.match(/^https?:\/\//)) {
          url = `https://${url}`;
        }

        const { tr, schema } = state;

        const linkMark = schema.marks.link.create({ href: url });
        const textNode = schema.text(linkText, [linkMark]);

        tr.replaceWith(range.from, range.to, textNode);

        return tr;
      },
    });

    const parentRules = this.parent?.() || [];
    rules.push(...parentRules);

    return rules;
  },

  addKeyboardShortcuts() {
    return {
      ...(this.parent?.() || {}),
      Space: ({ editor }) => {
        const { state } = editor;
        const { selection } = state;
        const { $from } = selection;

        const textNode = $from.nodeBefore;
        if (!textNode || textNode.type.name !== "text") {
          return false;
        }

        const textBefore = textNode.text || "";

        const linkMatch = textBefore.match(/\[([^\]]+)\]\(([^)]+)\)$/);
        if (linkMatch) {
          const linkText = linkMatch[1];
          let url = linkMatch[2];

          if (!url.match(/^https?:\/\//)) {
            url = `https://${url}`;
          }

          const matchLength = linkMatch[0].length;
          const start = $from.pos - matchLength;
          const end = $from.pos;

          editor
            .chain()
            .focus()
            .setTextSelection({ from: start, to: end })
            .deleteSelection()
            .insertContent(`<a href="${url}">${linkText}</a>`)
            .run();

          return true;
        }
        return false;
      },
    };
  },

  addPasteRules() {
    const rules = [];

    rules.push({
      find: /\[([^\]]+)\]\(([^)]+)\)/g,
      handler: ({ state, range, match }) => {
        const linkText = match[1];
        let url = match[2];

        if (!url.match(/^https?:\/\//)) {
          url = `https://${url}`;
        }

        const { tr, schema } = state;

        const linkMark = schema.marks.link.create({ href: url });
        const textNode = schema.text(linkText, [linkMark]);

        tr.replaceWith(range.from, range.to, textNode);

        return tr;
      },
    });

    const parentRules = this.parent?.() || [];
    rules.push(...parentRules);

    return rules;
  },
});

const defaultExtensions = [
  StarterKit.configure({
    codeBlock: false,
    bold: false,
  }),
  CustomBold,
  CodeBlockLowlight.configure({
    lowlight,
    defaultLanguage: "python",
    exitOnTripleEnter: true,
    exitOnArrowDown: true,
  }).extend({
    addKeyboardShortcuts() {
      return {
        ...(this.parent?.() || {}),
        Tab: () => {
          if (this.editor.isActive("codeBlock")) {
            this.editor.commands.insertContent("  ");
            return true;
          }
          return false;
        },
        "Shift-Tab": () => {
          if (this.editor.isActive("codeBlock")) {
            const { state } = this.editor;
            const { selection } = state;
            const { $from } = selection;
            const textBefore = $from.nodeBefore?.textContent || "";

            if (textBefore.endsWith("  ")) {
              this.editor.commands.deleteRange({
                from: $from.pos - 2,
                to: $from.pos,
              });
              return true;
            }
            return true;
          }
          return false;
        },
      };
    },
  }),
  CustomLink.configure({
    openOnClick: true,
    HTMLAttributes: {
      class: "cursor-pointer novel-editor-link",
      style: "color: #3b82f6; text-decoration: underline;",
      target: "_blank",
      rel: "noopener noreferrer",
    },
  }),
  TextAlign.configure({
    types: ["heading", "paragraph"],
  }),
  tiptapImage,
  TiptapUnderline,
  TaskList,
  TaskItem,
  Color.configure({
    types: ["textStyle"],
  }),
  TextStyle,
];

export { defaultExtensions };

const languages = [
  { name: "JavaScript", value: "javascript", aliases: ["js"] },
  { name: "Python", value: "python" },
  { name: "CSS", value: "css" },
  { name: "HTML", value: "html", aliases: ["xml"] },
];

const NovelEditor = ({
  initialValue,
  onChange,
  onBlur,
  onImageClick,
  className = "",
  editable = true,
  editorType = "default",
}) => {
  const [content, setContent] = useState(initialValue || null);
  const wrapperRef = useRef(null);
  const linkPopup = useLinkHoverPopup(wrapperRef);

  useEffect(() => {
    if (initialValue !== undefined) {
      setContent(initialValue);
    }
  }, [initialValue]);

  const [editorInstance, setEditorInstance] = useState(null);
  const languageMenuRef = useRef(null);
  const [showLinkInput, setShowLinkInput] = useState(false);
  const [linkUrl, setLinkUrl] = useState("");
  const linkInputRef = useRef(null);
  const linkSelectionRef = useRef(null);
  const linkSubmitButtonRef = useRef(null);
  const linkCancelButtonRef = useRef(null);
  const [isLanguageDropdownOpen, setIsLanguageDropdownOpen] = useState(false);
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [showLanguageDropDown, setShowLanguageDropDown] = useState(false);
  const [showFontSize, setShowFontSize] = useState(false);
  const [showAlign, setShowAlign] = useState(false);
  const [fontSize, setFontSize] = useState("Normal");
  const languageDropdownButtonRef = useRef(null);
  const languageDropdownRef = useRef(null);
  const shouldShowDropdownRef = useRef(false);
  const dropdownJustOpenedRef = useRef(false);
  const hasTypedInCurrentCodeBlockRef = useRef(false);
  const colorSelectionRef = useRef(null);
  const colorInputRef = useRef(null);
  const fontsizeRef = useClickOutside(() => {
    if (showFontSize) {
      setShowFontSize(false);
    }
  });
  const colorboxRef = useRef(null);
  const alignRef = useClickOutside(() => {
    setShowAlign(false);
  });

  const handleUpdate = ({ editor }) => {
    if (!editorInstance) {
      setEditorInstance(editor);
    }
    shouldShowDropdownRef.current = false;

    if (editor) {
      const { selection } = editor.state;
      const { $from } = selection;
      if ($from.parent.type.name === "codeBlock") {
        hasTypedInCurrentCodeBlockRef.current = true;
      }
    }

    setIsLanguageDropdownOpen((prev) => {
      if (prev) {
        return false;
      }
      return prev;
    });
    const json = editor.getJSON();
    if (onChange) {
      onChange(json);
    }
    setContent(json);
  };
  const toggleFontSize = () => {
    setShowFontSize(!showFontSize);
  };

  useEffect(() => {
    if (!editorInstance || !onBlur) return;

    const handleBlur = () => {
      onBlur();
    };

    editorInstance.on("blur", handleBlur);

    return () => {
      editorInstance.off("blur", handleBlur);
    };
  }, [editorInstance, onBlur]);

  useEffect(() => {
    if (!editorInstance || !onImageClick) return;
    const dom = editorInstance.view?.dom;
    if (!dom) return;

    const handleMouseDown = (event) => {
      const target = event.target;
      if (target && target.tagName === "IMG") {
        event.preventDefault();
        event.stopImmediatePropagation();
        onImageClick({
          src: target.getAttribute("src"),
          alt: target.getAttribute("alt") || "",
        });
      }
    };

    dom.addEventListener("mousedown", handleMouseDown, true);
    return () => {
      dom.removeEventListener("mousedown", handleMouseDown, true);
    };
  }, [editorInstance, onImageClick]);

  const getCurrentCodeBlockLanguage = (editor) => {
    if (!editor || !editor.isActive("codeBlock")) return null;
    try {
      const attrs = editor.getAttributes("codeBlock");
      return attrs.language || null;
    } catch (e) {
      return null;
    }
  };
  const presetColors = [
    // Neutrals
    "#ffffff",
    "#bbbbbb",
    "#888888",
    "#6B7280",
    "#444444",
    "#000000",

    // Reds
    "#facccc",
    "#f06666",
    "#EF4444",
    "#e60000",
    "#a10000",
    "#5c0000",

    // Oranges
    "#ffebcc",
    "#ffc266",
    "#F97316",
    "#ff9900",
    "#b26b00",
    "#663d00",

    // Yellows
    "#f8f8b2",
    "#ffff66",
    "#ffcc00",
    "#EAB308",
    "#b2b200",
    "#666600",

    // Greens
    "#b6f4b6",
    "#66b966",
    "#22C55E",
    "#008a00",
    "#006100",
    "#003700",

    // Blues
    "#bed9f5",
    "#02d6f2",
    "#3B82F6",
    "#0066cc",
    "#0047b2",
    "#002966",

    // Purples
    "#dcbef8",
    "#c285ff",
    "#8B5CF6",
    "#9933ff",
    "#6b24b2",
    "#3d1466",

    // Pinks
    "#FCE7F3",
    "#F9A8D4",
    "#ce87ae",
    "#fd86c1",
    "#DB2777",
    "#7f0035",
  ];

  const applyLink = useCallback(() => {
    if (!editorInstance) return;

    const input = linkInputRef.current;
    const currentUrl = input ? input.value.trim() : "";

    if (!currentUrl) {
      setShowLinkInput(false);
      setLinkUrl("");
      return;
    }

    const url = currentUrl.match(/^https?:\/\//)
      ? currentUrl
      : `https://${currentUrl}`;

    try {
      if (linkSelectionRef.current) {
        const { from, to } = linkSelectionRef.current;
        const docSize = editorInstance.state.doc.content.size;
        if (from <= docSize && to <= docSize && from <= to) {
          editorInstance
            .chain()
            .focus()
            .setTextSelection({ from, to })
            .setLink({ href: url })
            .run();
        } else {
          const { from: currFrom, to: currTo } = editorInstance.state.selection;
          if (currFrom !== currTo) {
            editorInstance
              .chain()
              .focus()
              .setTextSelection({ from: currFrom, to: currTo })
              .setLink({ href: url })
              .run();
          } else {
            editorInstance.chain().focus().setLink({ href: url }).run();
          }
        }
      } else {
        const { from, to } = editorInstance.state.selection;
        if (from !== to) {
          editorInstance
            .chain()
            .focus()
            .setTextSelection({ from, to })
            .setLink({ href: url })
            .run();
        } else {
          editorInstance
            .chain()
            .focus()
            .insertContent(`<a href="${url}">${url}</a>`)
            .run();
        }
      }
    } catch (error) {}

    linkSelectionRef.current = null;
    setShowLinkInput(false);
    setLinkUrl("");
  }, [editorInstance]);

  useEffect(() => {
    const input = linkInputRef.current;
    if (!input || !showLinkInput) return;

    input.focus();

    const handleKeyDown = (e) => {
      if (e.key === "Enter") {
        e.preventDefault();
        e.stopPropagation();
        applyLink();
      } else if (e.key === "Escape") {
        e.preventDefault();
        e.stopPropagation();
        setShowLinkInput(false);
        setLinkUrl("");
        linkSelectionRef.current = null;
      }
    };

    input.addEventListener("keydown", handleKeyDown, true);

    return () => {
      input.removeEventListener("keydown", handleKeyDown, true);
    };
  }, [showLinkInput, applyLink]);

  useEffect(() => {
    if (!showLinkInput) return;

    const prevent = (e) => {
      e.preventDefault();
      e.stopPropagation();
    };

    const add = (el, clickHandler) => {
      if (!el) return;
      el.addEventListener("click", clickHandler, true);
      el.addEventListener("mousedown", prevent, true);
    };

    const remove = (el, clickHandler) => {
      if (!el) return;
      el.removeEventListener("click", clickHandler, true);
      el.removeEventListener("mousedown", prevent, true);
    };

    const handleSubmit = (e) => {
      prevent(e);
      applyLink();
    };

    const handleCancel = (e) => {
      prevent(e);
      setShowLinkInput(false);
      setLinkUrl("");
      linkSelectionRef.current = null;
    };

    add(linkSubmitButtonRef.current, handleSubmit);
    add(linkCancelButtonRef.current, handleCancel);

    return () => {
      remove(linkSubmitButtonRef.current, handleSubmit);
      remove(linkCancelButtonRef.current, handleCancel);
    };
  }, [showLinkInput, applyLink]);

  useEffect(() => {
    if (!editorInstance) return;

    let cleanup = null;

    const timeoutId = setTimeout(() => {
      const button = languageDropdownButtonRef.current;
      if (!button) return;

      const handleClick = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsLanguageDropdownOpen((prev) => {
          const newValue = !prev;
          shouldShowDropdownRef.current = newValue;
          if (newValue) {
            dropdownJustOpenedRef.current = true;
            setTimeout(() => {
              dropdownJustOpenedRef.current = false;
            }, 100);
          }
          return newValue;
        });
      };

      button.addEventListener("click", handleClick, true);
      button.addEventListener(
        "mousedown",
        (e) => {
          e.preventDefault();
          e.stopPropagation();
        },
        true
      );

      cleanup = () => {
        button.removeEventListener("click", handleClick, true);
      };
    }, 100);

    return () => {
      clearTimeout(timeoutId);
      if (cleanup) cleanup();
    };
  }, [editorInstance]);

  useEffect(() => {
    if (!isLanguageDropdownOpen || !editorInstance) return;

    const handleClickOutside = (event) => {
      if (
        languageDropdownRef.current &&
        !languageDropdownRef.current.contains(event.target) &&
        !languageDropdownButtonRef.current?.contains(event.target)
      ) {
        setIsLanguageDropdownOpen(false);
      }
    };

    const handleEditorInteraction = (eventName) => {
      if (
        eventName === "selectionUpdate" ||
        eventName === "focus" ||
        eventName === "blur"
      ) {
        return;
      }

      if (dropdownJustOpenedRef.current) {
        return;
      }

      shouldShowDropdownRef.current = false;
      dropdownJustOpenedRef.current = false;
      setIsLanguageDropdownOpen((prev) => {
        if (prev) {
          return false;
        }
        return prev;
      });
    };

    editorInstance.on("update", () => handleEditorInteraction("update"));
    editorInstance.on("selectionUpdate", () =>
      handleEditorInteraction("selectionUpdate")
    );
    editorInstance.on("focus", () => handleEditorInteraction("focus"));
    editorInstance.on("blur", () => handleEditorInteraction("blur"));
    editorInstance.on("transaction", () =>
      handleEditorInteraction("transaction")
    );

    const handleKeyDown = (e) => {
      shouldShowDropdownRef.current = false;
      dropdownJustOpenedRef.current = false;

      if (editorInstance) {
        const { selection } = editorInstance.state;
        const { $from } = selection;
        if ($from.parent.type.name === "codeBlock") {
          hasTypedInCurrentCodeBlockRef.current = true;
        }
      }

      setIsLanguageDropdownOpen(false);
    };

    if (editorInstance.view?.dom) {
      editorInstance.view.dom.addEventListener("keydown", handleKeyDown, true);
    }
    const timeoutId = setTimeout(() => {
      document.addEventListener("mousedown", handleClickOutside, true);
    }, 0);

    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener("mousedown", handleClickOutside, true);
      editorInstance.off("update", handleEditorInteraction);
      editorInstance.off("selectionUpdate", handleEditorInteraction);
      editorInstance.off("focus", handleEditorInteraction);
      editorInstance.off("blur", handleEditorInteraction);
      editorInstance.off("transaction", handleEditorInteraction);
      if (editorInstance.view?.dom) {
        editorInstance.view.dom.removeEventListener(
          "keydown",
          handleKeyDown,
          true
        );
      }
    };
  }, [isLanguageDropdownOpen, editorInstance]);

  useEffect(() => {
    if (!editorInstance) return;

    const updateCodeBlockAttributes = () => {
      const editorElement = editorInstance.view.dom;
      const codeBlocks = editorElement.querySelectorAll("pre");

      const codeBlockNodes = [];
      editorInstance.state.doc.descendants((node, pos) => {
        if (node.type.name === "codeBlock") {
          codeBlockNodes.push({ node, pos, language: node.attrs.language });
        }
      });

      codeBlocks.forEach((pre, index) => {
        if (pre.style.position !== "relative") {
          pre.style.position = "relative";
        }
        let language = codeBlockNodes[index]?.language || null;

        if (!language) {
          language = pre.getAttribute("data-language");
        }

        if (!language) {
          const codeElement = pre.querySelector("code");
          if (codeElement) {
            const codeClasses = Array.from(codeElement.classList);
            const langClass = codeClasses.find(
              (cls) =>
                cls.startsWith("hljs-language-") || cls.startsWith("language-")
            );
            if (langClass) {
              language = langClass
                .replace("hljs-language-", "")
                .replace("language-", "");
            }
          }
        }

        if (language) {
          pre.setAttribute("data-language", language);
        }
      });
    };

    const handleUpdate = () => {
      setTimeout(updateCodeBlockAttributes, 10);
    };

    editorInstance.on("update", handleUpdate);
    editorInstance.on("selectionUpdate", handleUpdate);
    setTimeout(updateCodeBlockAttributes, 100);

    return () => {
      editorInstance.off("update", handleUpdate);
      editorInstance.off("selectionUpdate", handleUpdate);
    };
  }, [editorInstance]);

  useEffect(() => {
    if (!editorInstance) return;

    const input = colorInputRef.current;
    if (!input) {
      return;
    }

    const handleChange = (e) => {
      const color = e.target.value;

      if (!editorInstance) {
        return;
      }

      if (colorSelectionRef.current) {
        const { from, to } = colorSelectionRef.current;
        const docSize = editorInstance.state.doc.content.size;

        if (from <= docSize && to <= docSize) {
          editorInstance
            .chain()
            .focus()
            .setTextSelection({ from, to })
            .setColor(color)
            .run();
        }
      } else {
        const { from, to } = editorInstance.state.selection;
        editorInstance.chain().focus().setColor(color).run();
      }
    };

    input.addEventListener("change", handleChange);

    let lastValue = input.value;
    const intervalId = setInterval(() => {
      if (input && input.value !== lastValue) {
        lastValue = input.value;
      }
    }, 100);

    return () => {
      if (input) {
        input.removeEventListener("change", handleChange);
      }
      clearInterval(intervalId);
    };
  }, [editorInstance]);

  const ToolbarButton = ({ onClick, children, className = "" }) => {
    return (
      <button
        type="button"
        className="small novel-toolbar-btn "
        onMouseDown={(e) => {
          e.preventDefault();
          onClick?.();
        }}
      >
        {children}
      </button>
    );
  };

  useEffect(() => {
    if (!showAlign) return;

    const handleClickOutside = (event) => {
      if (alignRef.current && !alignRef.current.contains(event.target)) {
        setShowAlign(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside, true);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside, true);
    };
  }, [showAlign]);

  useEffect(() => {
    if (!showColorPicker) return;

    const handleClickOutside = (event) => {
      if (colorboxRef.current && !colorboxRef.current.contains(event.target)) {
        setShowColorPicker(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside, true);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside, true);
    };
  }, [showColorPicker]);

  return (
    <div
      ref={wrapperRef}
      className={`novel-editor-wrapper ${editorType} ${className}`}
    >
      <div>
        <div className="flex-rows relative flex-wrap novel-wrapper flex-align-center">
          <div
            ref={languageDropdownRef}
            className="novel-editor-language-dropdown"
            style={{ position: "relative" }}
          >
            <button
              ref={languageDropdownButtonRef}
              type="button"
              className="novel-editor-language-dropdown-trigger"
            >
              {(() => {
                const currentLang = getCurrentCodeBlockLanguage(editorInstance);
                const langObj = languages.find(
                  (l) =>
                    l.value === currentLang || l.aliases?.includes(currentLang)
                );
                return langObj ? langObj.name : "Language";
              })()}{" "}
              ▾
            </button>
            {isLanguageDropdownOpen && shouldShowDropdownRef.current ? (
              <div
                className="novel-editor-language-dropdown-menu"
                style={{ display: "flex" }}
              >
                {languages.map((lang) => (
                  <BubbleMenuItem
                    key={lang.value}
                    editor={editorInstance}
                    onClick={(editor) => {
                      editor
                        .chain()
                        .focus()
                        .setCodeBlock({ language: lang.value })
                        .run();

                      shouldShowDropdownRef.current = false;
                      setIsLanguageDropdownOpen(false);

                      if (languageMenuRef.current) {
                        languageMenuRef.current.hide();
                      }
                    }}
                    className={
                      getCurrentCodeBlockLanguage(editorInstance) ===
                        lang.value ||
                      (lang.aliases &&
                        lang.aliases.includes(
                          getCurrentCodeBlockLanguage(editorInstance)
                        ))
                        ? "active"
                        : ""
                    }
                  >
                    {lang.name}
                  </BubbleMenuItem>
                ))}
              </div>
            ) : null}
          </div>

          <div ref={fontsizeRef}>
            <button className="novel-toolbar-btn" onClick={toggleFontSize}>
              {fontSize} ▾
            </button>
            {showFontSize && (
              <div className="size-wrapper pl-2">
                <ToolbarButton
                  onClick={() => {
                    editorInstance?.chain()?.focus().setParagraph().run();
                    setFontSize("Normal");
                    toggleFontSize();
                  }}
                >
                  <em>Normal</em>
                </ToolbarButton>
                <ToolbarButton
                  onClick={() => {
                    editorInstance
                      ?.chain()
                      ?.focus()
                      .toggleHeading({ level: 1 })
                      .run();
                    setFontSize("H1");
                    setShowFontSize(false);
                  }}
                >
                  <em>H1</em>
                </ToolbarButton>

                <ToolbarButton
                  onClick={() => {
                    editorInstance
                      ?.chain()
                      ?.focus()
                      .toggleHeading({ level: 2 })
                      .run();
                    setFontSize("H2");
                    setShowFontSize(false);
                  }}
                >
                  <em>H2</em>
                </ToolbarButton>

                <ToolbarButton
                  onClick={() => {
                    editorInstance
                      ?.chain()
                      ?.focus()
                      .toggleHeading({ level: 3 })
                      .run();
                    setFontSize("H3");
                    setShowFontSize(false);
                  }}
                >
                  <em>H3</em>
                </ToolbarButton>
              </div>
            )}
          </div>
          <ToolbarButton
            onClick={() => editorInstance?.chain()?.focus().toggleBold().run()}
          >
            <strong>B</strong>
          </ToolbarButton>
          <ToolbarButton
            onClick={() =>
              editorInstance?.chain()?.focus().toggleItalic().run()
            }
          >
            <em>I</em>
          </ToolbarButton>

          <ToolbarButton
            onClick={() =>
              editorInstance?.chain()?.focus().toggleUnderline().run()
            }
          >
            <em>U</em>
          </ToolbarButton>
          <ToolbarButton
            onClick={() =>
              editorInstance?.chain()?.focus().toggleStrike().run()
            }
          >
            <s>S</s>
          </ToolbarButton>
          <div ref={alignRef} className="align-dropdown">
            <button
              className="novel-toolbar-btn"
              onClick={() => setShowAlign(!showAlign)}
            >
              Align ▾
            </button>

            {showAlign && (
              <div className="align-wrapper pl-2">
                <ToolbarButton
                  active={editorInstance?.isActive({ textAlign: "left" })}
                  onClick={() =>
                    editorInstance?.chain()?.focus().setTextAlign("left").run()
                  }
                >
                  <FontAwesomeIcon icon={faAlignLeft} />
                </ToolbarButton>
                <ToolbarButton
                  active={editorInstance?.isActive({ textAlign: "center" })}
                  onClick={() =>
                    editorInstance
                      ?.chain()
                      ?.focus()
                      .setTextAlign("center")
                      .run()
                  }
                >
                  <FontAwesomeIcon icon={faAlignCenter} />
                </ToolbarButton>
                <ToolbarButton
                  active={editorInstance?.isActive({ textAlign: "right" })}
                  onClick={() =>
                    editorInstance?.chain()?.focus().setTextAlign("right").run()
                  }
                >
                  <FontAwesomeIcon icon={faAlignRight} />
                </ToolbarButton>{" "}
              </div>
            )}
          </div>

          <ToolbarButton
            onClick={() => editorInstance?.chain()?.focus().toggleCode().run()}
          >
            <em>Code</em>
          </ToolbarButton>

          <ToolbarButton
            onClick={() => {
              editorInstance?.chain()?.focus().toggleCodeBlock().run();
            }}
          >
            <em>&lt;/&gt;</em>
          </ToolbarButton>

          <div ref={colorboxRef} className="color-dropdown">
            <ToolbarButton
              onClick={() => {
                if (!editorInstance?.state?.selection) return;

                const { from, to } = editorInstance?.state?.selection;

                colorSelectionRef.current = { from, to };

                setShowColorPicker((prev) => !prev);
              }}
            >
              <span
                className="font-semibold"
                style={{ borderBottom: "2px solid red" }}
              >
                A
              </span>
            </ToolbarButton>

            {showColorPicker && (
              <div
                className="novel-color-popup"
                onMouseDown={(e) => e.stopPropagation()}
                onClick={(e) => e.stopPropagation()}
              >
                <div className="novel-color-grid">
                  {presetColors.map((color) => (
                    <button
                      key={color}
                      type="button"
                      className="novel-color-swatch"
                      style={{ backgroundColor: color }}
                      onClick={() => {
                        const selection = colorSelectionRef.current;

                        if (selection) {
                          editorInstance
                            .chain()
                            .focus()
                            .setTextSelection(selection)
                            .setColor(color)
                            .run();
                        } else {
                          editorInstance.chain().focus().setColor(color).run();
                        }

                        setShowColorPicker(false);
                      }}
                    />
                  ))}
                </div>
              </div>
            )}
          </div>
          {showLinkInput ? (
            <div
              className="novel-editor-link-input"
              onMouseDown={(e) => {
                e.stopPropagation();
              }}
              onClick={(e) => {
                e.stopPropagation();
              }}
            >
              <input
                ref={linkInputRef}
                type="text"
                value={linkUrl}
                onChange={(e) => setLinkUrl(e.target.value)}
                placeholder="Enter URL..."
                className="novel-editor-link-input-field"
                autoFocus
              />
              <button
                ref={linkSubmitButtonRef}
                type="button"
                className="novel-editor-link-input-button"
              >
                ✓
              </button>
              <button
                ref={linkCancelButtonRef}
                type="button"
                className="novel-editor-link-input-button"
              >
                ✕
              </button>
            </div>
          ) : (
            <BubbleMenuItem
              editor={editorInstance}
              onClick={(editor) => {
                if (editor.isActive("link")) {
                  const { href } = editor.getAttributes("link");
                  setLinkUrl(href || "");
                  const { from, to } = editor.state.selection;
                  linkSelectionRef.current = { from, to };
                  setShowLinkInput(true);
                } else {
                  const { from, to } = editor.state.selection;
                  const selectedText = editor.state.doc
                    .textBetween(from, to)
                    .trim();

                  if (from !== to) {
                    linkSelectionRef.current = { from, to };
                  } else {
                    linkSelectionRef.current = null;
                  }

                  setLinkUrl(
                    selectedText.match(/^https?:\/\//) ? selectedText : ""
                  );
                  setShowLinkInput(true);
                }
              }}
              className={editorInstance?.isActive("link") ? "active" : ""}
            >
              <LinkIcon size={16} />
            </BubbleMenuItem>
          )}

          <BubbleMenuItem
            editor={editorInstance}
            onClick={(editor) => {
              if (editor.isActive("orderedList")) {
                editor
                  .chain()
                  .focus()
                  .liftListItem("listItem")
                  .toggleBulletList()
                  .run();
              } else {
                editor.chain().focus().toggleBulletList().run();
              }
            }}
          >
            <FontAwesomeIcon icon={faList} />
          </BubbleMenuItem>

          <BubbleMenuItem
            editor={editorInstance}
            onClick={(editor) => {
              if (editor.isActive("bulletList")) {
                editor
                  .chain()
                  .focus()
                  .liftListItem("listItem")
                  .toggleOrderedList()
                  .run();
              } else {
                editor.chain().focus().toggleOrderedList().run();
              }
            }}
          >
            <FontAwesomeIcon icon={faListOl} />
          </BubbleMenuItem>
          <BubbleMenuItem
            editor={editorInstance}
            onClick={(editor) =>
              editor.chain().focus().toggleBlockquote().run()
            }
          >
            <FontAwesomeIcon icon={faQuoteLeft} />
          </BubbleMenuItem>

          <BubbleMenuItem
            editor={editorInstance}
            onClick={(editor) => {
              const input = document.createElement("input");
              input.type = "file";
              input.accept = "image/*";
              input.onchange = async () => {
                if (input.files?.length) {
                  const file = input.files[0];
                  insertImageAsBase64(editor.view, file);
                }
              };
              input.click();
            }}
          >
            <ImageIcon size={16} />
          </BubbleMenuItem>
        </div>
      </div>
      {linkPopup}
      <EditorRoot>
        <EditorContent
          initialContent={content}
          extensions={defaultExtensions}
          className="novel-editor-content"
          editorProps={{
            attributes: {
              class:
                "prose prose-invert prose-sm sm:prose lg:prose-lg xl:prose-2xl dark:prose-invert focus:outline-none max-w-full text-gray-100",
            },
            handleKeyDown: (view, event) => {
              if (event.key === "Tab") {
                const { state } = view;
                const { selection } = state;
                const { $from } = selection;

                let isInCodeBlock = false;
                for (let depth = $from.depth; depth >= 0; depth--) {
                  const node = $from.node(depth);
                  if (node.type.name === "codeBlock") {
                    isInCodeBlock = true;
                    break;
                  }
                }

                if (isInCodeBlock) {
                  event.preventDefault();

                  if (event.shiftKey) {
                    const lineStart = $from.start($from.depth);
                    const textFromLineStart = state.doc.textBetween(
                      lineStart,
                      $from.pos
                    );
                    const leadingSpaces =
                      textFromLineStart.length -
                      textFromLineStart.trimStart().length;

                    if (leadingSpaces > 0) {
                      const spacesToRemove = Math.min(2, leadingSpaces);
                      const tr = state.tr.delete(
                        $from.pos - spacesToRemove,
                        $from.pos
                      );
                      view.dispatch(tr);
                    }
                  } else {
                    const tr = state.tr.insertText("  ", selection.from);
                    view.dispatch(tr);
                  }

                  return true;
                }
              }

              return false;
            },
            handlePaste: (view, event) => {
              const files = Array.from(event.clipboardData?.files || []);
              if (files.length === 0) return false;
              const imageFiles = files.filter((f) =>
                f.type.startsWith("image/")
              );
              const nonImageFiles = files.filter(
                (f) => !f.type.startsWith("image/")
              );
              if (nonImageFiles.length > 0) {
                event.preventDefault();
                errorToast("Only image files can be uploaded.");
                return true;
              }
              if (imageFiles.length > 0) {
                event.preventDefault();
                imageFiles.forEach((file) => insertImageAsBase64(view, file));
                return true;
              }
              return false;
            },
            handleDrop: (view, event, _slice, moved) => {
              if (moved) return false;
              const files = Array.from(event.dataTransfer?.files || []);
              if (files.length === 0) return false;
              const imageFiles = files.filter((f) =>
                f.type.startsWith("image/")
              );
              const nonImageFiles = files.filter(
                (f) => !f.type.startsWith("image/")
              );
              if (nonImageFiles.length > 0) {
                event.preventDefault();
                errorToast("Only image files can be uploaded.");
                return true;
              }
              if (imageFiles.length > 0) {
                event.preventDefault();
                const coords = view.posAtCoords({
                  left: event.clientX,
                  top: event.clientY,
                });
                const pos = coords?.pos ?? view.state.selection.from;
                imageFiles.forEach((file) =>
                  insertImageAsBase64(view, file, pos)
                );
                return true;
              }
              return false;
            },
          }}
          onCreate={({ editor }) => setEditorInstance(editor)}
          onUpdate={handleUpdate}
          editable={editable}
          autofocus={false}
        ></EditorContent>
      </EditorRoot>
    </div>
  );
};

export default NovelEditor;
